import React from "react";

function toNumericValue(value) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== "string") {
    return null;
  }

  const normalized = value.replace(/[^0-9.-]/g, "");
  if (!normalized) {
    return null;
  }

  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatSignedValue(value, precision = 2) {
  const numericValue = toNumericValue(value);
  if (numericValue === null) {
    return value ?? "";
  }

  const prefix = numericValue > 0 ? "+" : numericValue < 0 ? "-" : "";
  return `${prefix}${Math.abs(numericValue).toFixed(precision)}`;
}

function formatCurrentPrice(currentPrice, currency, precision = 2) {
  const numericValue = toNumericValue(currentPrice);

  if (numericValue === null) {
    return currentPrice ?? "—";
  }

  if (currency) {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency,
      minimumFractionDigits: precision,
      maximumFractionDigits: precision
    }).format(numericValue);
  }

  return new Intl.NumberFormat("en-CA", {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision
  }).format(numericValue);
}

function directionFromDelta(delta, trend) {
  if (trend === "positive" || trend === "negative" || trend === "neutral") {
    return trend;
  }

  const numericDelta = toNumericValue(delta);
  if (numericDelta === null) {
    return "neutral";
  }

  if (numericDelta > 0) {
    return "positive";
  }

  if (numericDelta < 0) {
    return "negative";
  }

  return "neutral";
}

function deltaIconClass(direction) {
  if (direction === "positive") {
    return "bi-arrow-up-right";
  }

  if (direction === "negative") {
    return "bi-arrow-down-right";
  }

  return "bi-dash-lg";
}

export default function Ticker({
  title = "Ticker",
  currentPrice = "—",
  priceDelta = null,
  priceDeltaPercent = null,
  currency = null,
  precision = 2,
  unit = "",
  detail = "",
  trend = "neutral",
  className = "",
  ariaLabel = ""
}) {
  const direction = directionFromDelta(priceDelta, trend);
  const currentPriceLabel = formatCurrentPrice(currentPrice, currency, precision);
  const deltaLabel = priceDelta === null || priceDelta === undefined ? "" : formatSignedValue(priceDelta, precision);
  const deltaPercentLabel = priceDeltaPercent === null || priceDeltaPercent === undefined
    ? ""
    : `${formatSignedValue(priceDeltaPercent, precision)}%`;

  const classes = ["sh-ticker", `is-${direction}`, className].filter(Boolean).join(" ");
  const deltaClasses = ["sh-ticker-delta", `is-${direction}`].join(" ");

  return (
    <article className={classes} aria-label={ariaLabel || `${title} price ticker`}>
      <div className="sh-ticker-copy">
        <span className="sh-ticker-title">{title}</span>
        {detail ? <span className="sh-ticker-detail">{detail}</span> : null}
      </div>

      <div className="sh-ticker-values">
        <span className="sh-ticker-price">
          {currentPriceLabel}
          {unit ? <span className="sh-ticker-price-unit">{unit}</span> : null}
        </span>
        <span className={deltaClasses}>
          <i className={`bi ${deltaIconClass(direction)} me-1`} aria-hidden="true" />
          {deltaLabel}
          {deltaPercentLabel ? <span className="sh-ticker-delta-percent ms-1">({deltaPercentLabel})</span> : null}
        </span>
      </div>
    </article>
  );
}